import {useLocalisedDates} from "~/composables/useLocalisedDates";
import {computed} from "vue";

export function useFormData() {
    const {shortDate} = useLocalisedDates();
    const parseDateFormatTokens = (input, luxon) => {
        if (!input || typeof input !== 'string') return input

        return input.replace(/{{df::(.*?):(.*?)}}/g, (_, format, date) => {
            try {
                const dt = luxon.fromISO(date, {zone: 'utc'}).toLocal()
                return dt.toFormat(format.trim())
            } catch (e) {
                console.warn(`Failed to parse token: {{df::${format}:${date}}}`, e)
                return ''
            }
        })
    }

    const radiusSelect = computed(() => {
        return [
            {label: "5 miles", value: 5},
            {label: "10 miles", value: 10},
            {label: "20 miles", value: 20},
            {label: "35 miles", value: 35},
            {label: "50 miles", value: 50},
        ]
    });
    const playersPerTeamSelect = computed(() => {
        return [
            {value: 1, text: '1 (Singles)'},
            {value: 2, text: '2 (Doubles)'},
            {value: 3, text: '3'},
            {value: 4, text: '4'},
            {value: 5, text: '5'},
            {value: 6, text: '6'},
            {value: 7, text: '7'},
            {value: 8, text: '8'},
            {value: 9, text: '9'},
            {value: 10, text: '10'},
            {value: 11, text: '11'},
            {value: 12, text: '12'},
            {value: 13, text: '13'},
            {value: 14, text: '14'},
            {value: 15, text: '15'},
            {value: 16, text: '16'},
            {value: 17, text: '17'},
            {value: 18, text: '18'},
            {value: 19, text: '19'},
            {value: 20, text: '20'},
        ]
    });

    const timeSelect = computed(() => {
        return [
            '00:00',
            '01:00',
            '02:00',
            '03:00',
            '04:00',
            '05:00',
            '06:00',
            '07:00',
            '08:00',
            '09:00',
            '10:00',
            '11:00',
            '12:00',
            '13:00',
            '14:00',
            '15:00',
            '16:00',
            '17:00',
            '18:00',
            '19:00',
            '20:00',
            '21:00',
            '22:00',
            '23:00',
        ]
    });

    const hourSelect = computed(() => {
        return Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
    });

    const minuteSelect = computed(() => {
        return Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));
    });

    const suggestedTime = computed(() => {
        const now = useNuxtApp().$luxon.local();
        const nextHour = now.plus({ hours: 1 }).startOf('hour');
        return nextHour.toFormat('HH:mm');
    });

    const suggestedTimeSelectHalfHour = computed(() => {
        const now = useNuxtApp().$luxon.local();
        const minutes = now.minute;
        const roundedMinutes = minutes < 30 ? 30 : 0;
        const hoursToAdd = minutes < 30 ? 0 : 1;
        const nextHalfHour = now.plus({ hours: hoursToAdd }).startOf('hour').plus({ minutes: roundedMinutes });
        return nextHalfHour.toFormat('HH:mm');
    });

    const rolloverOptions = [
        {value: 0, label: 'No promotion/relegation'},
        {value: 1, label: '1 up, 1 down'},
        {value: 2, label: '2 up, 2 down'},
        {value: 3, label: '3 up, 3 down'},
    ];
    const challengeRejectionRuleToText = (rule, moveDownPlaces) => {
        switch (rule) {
            case 0:
                return 'No action';
            case 1:
                return `-${moveDownPlaces} place${moveDownPlaces > 1 ? 's' : ''}`;
            case 2:
                return 'Forfeit match';
            default:
                return 'Unknown rule';
        }
    }

    // What a win does to the two places (LadderWinMovement on the API). A ladder
    // from before the setting existed has no value, which is "goes above".
    const winMovementOptions = [
        {value: 0, label: 'Winner goes above the loser', text: 'The winner takes the loser\'s place. The loser and everyone between them move down one.'},
        {value: 1, label: 'Winner and loser swap places', text: 'The two trade places. Nobody else moves.'},
    ];
    const isSwapLadder = (ladder) => (ladder?.winMovement ?? 0) === 1;
    const winMovementToText = (ladder) => winMovementOptions[isSwapLadder(ladder) ? 1 : 0].label;

    const challengeRejectionRuleExplanation = (ladder) => {
        if (!ladder || !ladder.challengeRejectionRule) return null;
        switch (ladder.challengeRejectionRule) {
            case 0:
                return 'No action will be taken against you';
            case 1:
                return `You will be moved down ${ladder.challengeRejectionMoveDownPlaces} place${ladder.challengeRejectionMoveDownPlaces > 1 ? 's' : ''}`;
            case 2:
                return 'You will forfeit the match';
            default:
                return null;
        }
    }

    const inviteScopeOptions = [
        {value: 0, label: 'Anyone'},
        {value: 1, label: 'Club players only'},
    ];

    const inviteJoinSettingOptions = [
        {value: 0, label: 'Auto join'},
        {value: 1, label: 'Require admin approval'},
    ];

    // 0 Visible (everyone) · 2 Visible to members only (hidden from non-members
    // on the page, still joinable via a link sent to them) · 1 Hidden (link only).
    const inviteEntityPageSettingOptions = (entityTypeName = 'entity') => [
        {value: 0, label: `Visible on ${entityTypeName} page`},
        {value: 2, label: `Visible to club players`},
        {value: 1, label: `Hidden from ${entityTypeName} page`},
    ];

    const inviteReferenceTypeOptions = [
        {value: 0, label: 'Club'},
        {value: 1, label: 'Ladder'},
        {value: 2, label: 'Box league cycle'},
        {value: 4, label: 'Tournament'},
        {value: 5, label: 'Meet'},
        {value: 6, label: 'Americano cycle'},
    ];

    const inviteReferenceTypeToText = (type) => {
        switch (type) {
            case 0: return 'Club';
            case 1: return 'Ladder';
            case 2: return 'Box league cycle';
            case 3: return 'Player';
            case 4: return 'Tournament';
            case 5: return 'Meet';
            case 6: return 'Americano cycle';
            default: return 'Unknown';
        }
    };

    const challengeRejectionRules = [
        {value: 0, label: challengeRejectionRuleToText(0)},
        {value: 1, label: challengeRejectionRuleToText(1, 'x')},
        {value: 2, label: challengeRejectionRuleToText(2)}
    ];

    const tournamentFormatSelect = computed(() => {
        return [
            {value: 1, title: 'Knockout', text: 'Pure knockout bracket from start to finish'},
            {value: 2, title: 'Groups then Knockout', text: 'Round-robin groups followed by knockout stage'}
        ]
    });

    const seedingStrategySelect = computed(() => {
        return [
            {value: 1, title: 'Manual', text: 'Manually assign seed numbers to teams'},
            {value: 2, title: 'Random', text: 'System randomly assigns seeds'},
            {value: 3, title: 'Group finishing position', text: 'Seeds based on group stage finish positions'}
        ]
    });

    const cycleItemTitle = (item) => {
        if (!item || !item.id) return '';
        if (item.startTime && item.endTime) {
            return (
                item.name +
                ' : ' +
                item.startDate +
                ' to ' +
                item.endDate +
                (item.isActive ? ' (active)' : ' (inactive)')
            );
        }
        return (
            item.name +
            ' : ' +
            shortDate(item.startDate) +
            ' to ' +
            shortDate(item.endDate) +
            (item.isActive ? ' (active)' : ' (inactive)')
        );
    };

    /**
     * Where each team finished last cycle, for the promoted/relegated arrow and
     * the "last cycle" tooltip in the admin players panel. The API says it on
     * each team row (previousDivisionSort / Name / Position / TotalPoints,
     * 13 Oct 2026); it used to send the whole previous cycle and this matched
     * the teams up by their players. The names set here are unchanged.
     */
    function transformCycle(cycle) {
        if (!cycle || !cycle.rolloverCycleId) return cycle;
        cycle.leagueCycleDivisions.forEach((division, divisionIndex) => {
            division.teams.forEach((team) => {
                const lastDivisionIndex = team.previousDivisionSort ?? 0;
                team.divisionChange = lastDivisionIndex - divisionIndex;
                team.lastPoints = team.previousTotalPoints ?? 0;
                team.lastPosition = team.previousPosition ?? 0;
                team.lastDivision = lastDivisionIndex;
                team.lastDivisionName = team.previousDivisionName ?? null;
            });
            // No re-sort: the API's team order is authoritative — it carries the
            // division's persisted initial sort (wizard/rollover/rearrangement order).
        });
        return cycle;
    }


    return {
        inviteScopeOptions,
        inviteJoinSettingOptions,
        inviteEntityPageSettingOptions,
        inviteReferenceTypeOptions,
        inviteReferenceTypeToText,
        challengeRejectionRules,
        challengeRejectionRuleToText,
        rolloverOptions,
        playersPerTeamSelect,
        timeSelect,
        hourSelect,
        minuteSelect,
        suggestedTime,
        suggestedTimeSelectHalfHour,
        radiusSelect,
        cycleItemTitle,
        transformCycle,
        parseDateFormatTokens,
        challengeRejectionRuleExplanation,
        winMovementOptions,
        isSwapLadder,
        winMovementToText,
        tournamentFormatSelect,
        seedingStrategySelect
    };
}

